import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { UserData } from "@/utils/models/UserData";
import { UserContextType } from "./UserContextType";

const UserContext = createContext<UserContextType | undefined>(undefined);
const AUTH_STORAGE_KEYS = ["harenaCowonder", "harenaCowonderAuth"] as const;

function readStoredUser(): UserData | null {
  try {
    for (const key of AUTH_STORAGE_KEYS) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;

      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return parsed as UserData;
        }
      } catch {
        return { auth: raw } as UserData;
      }
    }
  } catch (e) {
    console.error("[UserContext] erro ao ler localStorage:", e);
  }

  return null;
}

function persistUserData(data: UserData | null) {
  if (!data) {
    for (const key of AUTH_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
    return;
  }

  const serialized = JSON.stringify(data);
  localStorage.setItem("harenaCowonder", serialized);
  localStorage.setItem("harenaCowonderAuth", serialized);
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userData, setUserDataState] = useState<UserData | null>(null);

  useEffect(() => {
    const stored = readStoredUser();
    if (stored) {
      setUserDataState(stored);
    }
  }, []);

  const logout = useCallback(() => {
    try {
      persistUserData(null);
      setUserDataState(null);
    } catch (e) {
      console.error("[UserContext] erro ao deslogar:", e);
    }
  }, []);

  const setUserData = useCallback(
    (updater: React.SetStateAction<UserData | null>) => {
      setUserDataState((prev) => {
        const next =
          typeof updater === "function"
            ? (updater as (current: UserData | null) => UserData | null)(prev)
            : updater;

        try {
          persistUserData(next);
        } catch (e) {
          console.error("[UserContext] erro ao salvar userData:", e);
        }

        return next;
      });
    },
    [],
  );

  return (
    <UserContext.Provider value={{ userData, setUserData, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
